import { useEffect, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../components/Skeleton.js";
import { Link } from "react-router-dom";
import {
  api,
  ApiError,
  AppSettings,
  OverviewSnapshot,
  SaaSOverview,
  ReplayJob,
  loadSession,
  AggregateDomain,
  AggregateDomainReconciliationRun
} from "../lib/api.js";
import { useTableState } from "../lib/useTableState.js";
import TableToolbar from "../components/TableToolbar.js";

type WorkspaceDashboard = Awaited<ReturnType<typeof api.workspaceDashboard>>;
type AggregateBundle = {
  domains: AggregateDomain[];
  reconciliation: { latest: AggregateDomainReconciliationRun | null; varianceCount: number };
  lastRollupRun: { domainId: string; status: string; completedAt?: string; startedAt: string; mode: string } | null;
};
type TallySummary = {
  // Auto-generated primitive tallies derived from synced tracker / aggregate
  // metadata. Counts are scoped to the active workspace where one is bound;
  // platform users see the cross-tenant view.
  tallyCount: number;
  bucketCount: number;
  bucketTotal: number;
  indicatorCount: number;
  publishedIndicatorCount: number;
};

type Stat = { label: string; value: string | number; hint?: string };

function statsFromSaas(s: SaaSOverview): Stat[] {
  return [
    { label: "Workspaces",      value: s.workspaceCount,       hint: `${s.activeWorkspaceCount} active` },
    { label: "Datasets",        value: s.datasetCount },
    { label: "Public portals",  value: s.activePortalCount,    hint: "active" },
    { label: "Vaulted secrets", value: s.configuredSecretCount, hint: `${s.restrictedSecretCount} restricted` },
    { label: "Onboarding",      value: s.onboardingStage },
    { label: "Subscription",    value: s.subscriptionStatus }
  ];
}

function statsFromOverview(o: OverviewSnapshot): Stat[] {
  return [
    { label: "Programs",     value: Number(o.metadataProgramCount ?? 0) },
    { label: "Dimensions",   value: Number(o.metadataDimensionCount ?? 0) },
    { label: "Tallies",      value: Number(o.metadataTallyCount ?? 0) },
    { label: "Indicators",   value: Number(o.metadataIndicatorCount ?? 0) },
    { label: "Jobs queued",  value: Number(o.replayJobsQueued ?? 0) },
    { label: "Jobs running", value: Number(o.replayJobsRunning ?? 0) },
    { label: "Jobs failed",  value: Number(o.replayJobsFailed ?? 0) }
  ];
}

function relativeTime(iso?: string): string {
  if (!iso) return "never";
  const now = Date.now();
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return "—";
  const deltaMs = now - then;
  if (deltaMs < 0) return "soon";
  const mins = Math.floor(deltaMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

type JobShape = ReplayJob & { requestedAt?: string; completedAt?: string; workspaceId?: string };

function syncHealth(jobs: ReplayJob[]): {
  lastSuccess?: string;
  lastSuccessRel: string;
  activeCount: number;
  pendingCount: number;
  failed24h: number;
  total24h: number;
  errorRatePct: number;
} {
  const jobList = jobs as JobShape[];
  const cutoff = Date.now() - 24 * 3600 * 1000;
  const within24 = jobList.filter((j) => {
    const t = Date.parse(j.createdAt ?? j.requestedAt ?? "");
    return Number.isFinite(t) && t >= cutoff;
  });
  const successes = jobList.filter((j) => j.status === "succeeded" || j.status === "completed");
  successes.sort((a, b) => {
    const at = Date.parse(a.updatedAt ?? a.completedAt ?? "") || 0;
    const bt = Date.parse(b.updatedAt ?? b.completedAt ?? "") || 0;
    return bt - at;
  });
  const lastSuccess = successes[0]?.updatedAt ?? successes[0]?.completedAt;
  const failed24h = within24.filter((j) => j.status === "failed" || j.status === "dead-lettered").length;
  const active = jobList.filter((j) => j.status === "running").length;
  const pending = jobList.filter((j) => j.status === "queued").length;
  const total24h = within24.length;
  const errorRatePct = total24h === 0 ? 0 : Math.round((failed24h / total24h) * 100);
  return {
    lastSuccess,
    lastSuccessRel: relativeTime(lastSuccess),
    activeCount: active,
    pendingCount: pending,
    failed24h,
    total24h,
    errorRatePct
  };
}

function jobPill(status?: string) {
  switch (status) {
    case "succeeded": return <span className="pill pill-ok">Succeeded</span>;
    case "running":   return <span className="pill pill-info">Running</span>;
    case "queued":    return <span className="pill pill-muted">Queued</span>;
    case "failed":    return <span className="pill pill-err">Failed</span>;
    default:          return <span className="pill pill-muted">{status ?? "—"}</span>;
  }
}

function syncJobHint(job: ReplayJob | null | undefined): string {
  if (!job) return "never";
  const result = (job as ReplayJob & { result?: Record<string, unknown> }).result;
  if (!result || typeof result !== "object") return job.status ?? "unknown";
  const trackerEvents = typeof result.trackerEvents === "number" ? result.trackerEvents : null;
  const ingested = typeof result.ingested === "number" ? result.ingested : null;
  if (trackerEvents !== null) return `${job.status} · ${trackerEvents} tracker events`;
  if (ingested !== null) return `${job.status} · ${ingested} ingested`;
  return job.status ?? "unknown";
}

function jobTimestamp(job: ReplayJob | null | undefined): string | undefined {
  const candidate = job as (ReplayJob & { completedAt?: string; startedAt?: string; requestedAt?: string }) | null | undefined;
  return candidate?.completedAt ?? candidate?.startedAt ?? candidate?.requestedAt;
}

export default function Dashboard() {
  const [overview, setOverview] = useState<OverviewSnapshot | null>(null);
  const [saas, setSaas] = useState<SaaSOverview | null>(null);
  const [jobs, setJobs] = useState<ReplayJob[]>([]);
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [wsDashboard, setWsDashboard] = useState<WorkspaceDashboard | null>(null);
  const [aggregate, setAggregate] = useState<AggregateBundle | null>(null);
  const [tally, setTally] = useState<TallySummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const session = loadSession();
    const wsId = session?.workspaceId;
    // /api/saas/overview is platform-operator-only. Workspace users get
    // 403 + a misleading "Platform-wide SaaS overview is platform-operator
    // only" alert if we surface the failure. Only call it when the
    // session is actually a platform admin; otherwise resolve null.
    const isPlatformAdmin = session?.role === "admin" && !session?.workspaceId;
    Promise.all([
      api.overview(),
      isPlatformAdmin ? api.saasOverview().catch(() => null) : Promise.resolve(null),
      api.jobs().catch(() => ({ jobs: [] })),
      api.settings().catch(() => null),
      wsId ? api.workspaceDashboard(wsId).catch(() => null) : Promise.resolve(null),
      api.aggregateDomains().catch(() => null),
      api.aggregateReconciliation().catch(() => null),
      api.request<{ runs: Array<{ domainId: string; status: string; mode: string; startedAt: string; completedAt?: string }>; facts: unknown[] }>("/api/submitted-aggregate/rollups").catch(() => null),
      api.listTallies().catch(() => ({ tallies: [] })),
      api.listBuckets().catch(() => ({ buckets: [] })),
      api.listIndicators().catch(() => ({ indicators: [] }))
    ])
      .then(([o, s, j, st, wd, ad, ar, rr, tl, bk, ind]) => {
        if (cancelled) return;
        setOverview(o);
        setSaas(s);
        setJobs(j.jobs ?? []);
        setSettings(st);
        setWsDashboard(wd);
        if (ad) {
          const sortedRuns = (rr?.runs ?? []).slice().sort((a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt));
          setAggregate({
            domains: ad.domains ?? [],
            reconciliation: {
              latest: ar?.latest ?? null,
              varianceCount: (ar?.comparisons ?? []).filter((c) => c.variance !== 0).length
            },
            lastRollupRun: sortedRuns[0] ?? null
          });
        }
        // Workspace-scoped tally summary. The API already filters by the
        // session workspace; for platform users with no workspace bound
        // these are cross-tenant counts. notAggregable rows are excluded
        // from the headline count — they're metadata-census markers, not
        // actionable variables, and would inflate the number misleadingly.
        const allTallies = wsId ? tl.tallies.filter((t) => !t.workspaceId || t.workspaceId === wsId) : tl.tallies;
        const tallies = allTallies.filter((t) => !(t as { notAggregable?: boolean }).notAggregable);
        const buckets = wsId ? bk.buckets.filter((b) => !b.workspaceId || b.workspaceId === wsId) : bk.buckets;
        const indicators = wsId ? ind.indicators.filter((i) => !i.workspaceId || i.workspaceId === wsId) : ind.indicators;
        setTally({
          tallyCount: tallies.length,
          bucketCount: buckets.length,
          bucketTotal: buckets.reduce((sum, b) => sum + (Number(b.value) || 0), 0),
          indicatorCount: indicators.length,
          publishedIndicatorCount: indicators.filter((i) => i.published).length
        });
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const recentJobs = jobs.slice(0, 6);
  const noProfile = !loading && settings !== null && (settings.serviceProfiles?.length ?? 0) === 0;
  const health = syncHealth(jobs);
  const trackerWatermarks = wsDashboard?.syncStatus?.trackerWatermarks ?? [];
  const aggregateWatermarks = wsDashboard?.syncStatus?.aggregateWatermarks ?? [];
  const latestTrackerWatermark = trackerWatermarks
    .slice()
    .sort((a, b) => Date.parse(b.updatedAt ?? b.lastUpdated) - Date.parse(a.updatedAt ?? a.lastUpdated))[0];
  const recentFailures = (jobs as (ReplayJob & { completedAt?: string; requestedAt?: string })[])
    .filter((j) => j.status === "failed" || j.status === "dead-lettered")
    .slice(0, 5);

  const failuresTable = useTableState(recentFailures, {
    searchFields: ["id", "type", "status"],
    initialPageSize: 25
  });
  const recentJobsTable = useTableState(recentJobs, {
    searchFields: ["id", "type", "status"],
    initialPageSize: 25
  });

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>TallyStream · DHIS2 ⇄ Warehouse</h1>
          <p>
            Streams metadata and tally values from your connected DHIS2 servers into the
            TallyStream warehouse, then forwards downstream. Monitor freshness, retry failed
            jobs, and jump into the workflows you own.
          </p>
        </div>
        <div className="actions">
          <Link to="/app/instances" className="pill pill-info">
            Connected DHIS2 instances
          </Link>
          <Link to="/app/jobs" className="pill pill-muted">
            Jobs & retries
          </Link>
        </div>
      </header>

      {error && <div className="alert alert-error">{error}</div>}

      {noProfile && (
        <section className="section">
          <div className="card" style={{ borderLeft: "4px solid var(--accent, #2563eb)" }}>
            <div className="card-head"><strong>Get started</strong></div>
            <div className="card-body">
              <p style={{ marginTop: 0 }}>
                No DHIS2 instance is connected yet. Complete these steps to start monitoring tally streams.
              </p>
              <ol style={{ lineHeight: 1.8, marginBottom: 0 }}>
                <li><Link to="/app/instances">Add a DHIS2 instance</Link> (URL, auth mode, credentials)</li>
                <li>Install the TallyStream app in your DHIS2 UI and use <em>Discover app</em> to provision an app client</li>
                <li><Link to="/app/settings">Pick defaults</Link> (active instance, period, org unit)</li>
                <li>Run your first ingestion from the <Link to="/app/jobs">Jobs</Link> page</li>
              </ol>
            </div>
          </div>
        </section>
      )}

      {!noProfile && (
        <section className="section">
          <div className="section-head"><h2>Sync health</h2></div>
          <div className="stat-grid">
            <div className="stat">
              <div className="stat-label">Last successful sync</div>
              <div className="stat-value">{health.lastSuccessRel}</div>
              {health.lastSuccess && <div className="stat-hint">{new Date(health.lastSuccess).toLocaleString()}</div>}
            </div>
            <div className="stat">
              <div className="stat-label">Active jobs</div>
              <div className="stat-value">{health.activeCount}</div>
              <div className="stat-hint">{health.pendingCount} queued</div>
            </div>
            <div className="stat">
              <div className="stat-label">Runs (24h)</div>
              <div className="stat-value">{health.total24h}</div>
              <div className="stat-hint">{health.failed24h} failed</div>
            </div>
            <div className="stat">
              <div className="stat-label">Error rate (24h)</div>
              <div className="stat-value" style={{ color: health.errorRatePct > 10 ? "#b91c1c" : undefined }}>
                {health.errorRatePct}%
              </div>
            </div>
            {wsDashboard && (
              <>
                <div className="stat">
                  <div className="stat-label">Tracker watermark</div>
                  <div className="stat-value">{relativeTime(latestTrackerWatermark?.updatedAt ?? latestTrackerWatermark?.lastUpdated)}</div>
                  <div className="stat-hint">{latestTrackerWatermark?.scope ?? "not set"}</div>
                </div>
                <div className="stat">
                  <div className="stat-label">Tracker sync</div>
                  <div className="stat-value">{relativeTime(jobTimestamp(wsDashboard.syncStatus.lastTrackerDataSync))}</div>
                  <div className="stat-hint">{syncJobHint(wsDashboard.syncStatus.lastTrackerDataSync)}</div>
                </div>
                <div className="stat">
                  <div className="stat-label">Aggregate sync</div>
                  <div className="stat-value">{relativeTime(jobTimestamp(wsDashboard.syncStatus.lastAggregateDataSync))}</div>
                  <div className="stat-hint">{syncJobHint(wsDashboard.syncStatus.lastAggregateDataSync)}</div>
                </div>
                <div className="stat">
                  <div className="stat-label">Watermarks</div>
                  <div className="stat-value">{wsDashboard.syncStatus.watermarksCount}</div>
                  <div className="stat-hint">
                    {trackerWatermarks.length} tracker · {aggregateWatermarks.length} aggregate
                    {wsDashboard.syncStatus.legacyGlobalTrackerWatermark ? " · legacy global present" : ""}
                  </div>
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {recentFailures.length > 0 && (
        <section className="section">
          <div className="section-head">
            <h2>Recent failures</h2>
            <Link className="linkbtn" to="/app/jobs">View all</Link>
          </div>
          <div className="table-scroll">
            <TableToolbar
            hideExport
              search={failuresTable.search}
              setSearch={failuresTable.setSearch}
              page={failuresTable.page}
              setPage={failuresTable.setPage}
              pageSize={failuresTable.pageSize}
              setPageSize={failuresTable.setPageSize}
              totalPages={failuresTable.totalPages}
              totalRows={failuresTable.totalRows}
            />
            <table className="data-table">
              <thead>
                <tr><th>ID</th><th>Type</th><th>When</th><th>Error</th></tr>
              </thead>
              <tbody>
                {failuresTable.paged.map((j) => {
                  const when = j.updatedAt ?? j.completedAt ?? j.createdAt ?? j.requestedAt;
                  const err = (j as { error?: unknown }).error;
                  return (
                    <tr key={j.id}>
                      <td><code>{j.id.slice(0, 12)}…</code></td>
                      <td>{j.type ?? "—"}</td>
                      <td className="soft">{when ? new Date(when).toLocaleString() : "—"}</td>
                      <td className="soft" style={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis" }}>
                        {typeof err === "string" ? err : err ? JSON.stringify(err).slice(0, 120) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {aggregate && aggregate.domains.length > 0 && (
        <section className="section">
          <div className="section-head">
            <h2>Aggregate engine</h2>
            <Link className="linkbtn" to="/admin/aggregate-domains">Open Studio</Link>
          </div>
          <div className="stat-grid">
            <div className="stat">
              <div className="stat-label">Domains</div>
              <div className="stat-value">{aggregate.domains.filter((d) => d.active && !d.archived).length}</div>
              <div className="stat-hint">{aggregate.domains.length} total · {aggregate.domains.filter((d) => d.archived).length} archived</div>
            </div>
            <div className="stat">
              <div className="stat-label">Last rollup run</div>
              <div className="stat-value">{relativeTime(aggregate.lastRollupRun?.completedAt ?? aggregate.lastRollupRun?.startedAt)}</div>
              <div className="stat-hint">
                {aggregate.lastRollupRun
                  ? `${aggregate.lastRollupRun.mode} · ${aggregate.lastRollupRun.status}`
                  : "no runs yet"}
              </div>
            </div>
            <div className="stat">
              <div className="stat-label">Last reconciliation</div>
              <div className="stat-value">{relativeTime(aggregate.reconciliation.latest?.completedAt ?? aggregate.reconciliation.latest?.startedAt)}</div>
              <div className="stat-hint">
                {aggregate.reconciliation.latest
                  ? `${aggregate.reconciliation.latest.status} · ${aggregate.reconciliation.latest.sourceFactVarianceCount} variances`
                  : "never run"}
              </div>
            </div>
            <div className="stat">
              <div className="stat-label">Tracker vs submitted variance</div>
              <div className="stat-value" style={{ color: aggregate.reconciliation.varianceCount > 0 ? "#b91c1c" : undefined }}>
                {aggregate.reconciliation.varianceCount}
              </div>
              <div className="stat-hint"><Link to="/admin/reconciliation">Reconciliation & integrity</Link></div>
            </div>
          </div>
        </section>
      )}

      {tally && (
        <section className="section">
          <div className="section-head">
            <h2>Tally engine</h2>
            <span className="actions">
              <Link className="linkbtn" to={loadSession()?.workspaceId ? "/app/tallying/tallymetrics" : "/admin/data/tallymetrics"}>
                TallyMetrics
              </Link>
              <Link className="linkbtn" to={loadSession()?.workspaceId ? "/app/tallying/rollups" : "/admin/data/rollups-indicators"}>
                Rollups & indicators
              </Link>
            </span>
          </div>
          {tally.tallyCount === 0 && tally.indicatorCount === 0 ? (
            <div className="card" style={{ borderLeft: "4px solid var(--accent, #2563eb)" }}>
              <div className="card-body">
                <p style={{ marginTop: 0 }}>
                  No tally metrics yet. Pair a DHIS2 instance and sync tracker / aggregate metadata —
                  TallyMetrics auto-generates primitive metrics from the synced metadata.
                </p>
                <Link className="linkbtn" to={loadSession()?.workspaceId ? "/app/administration/connections/instances" : "/admin/administration/dhis2/instances"}>
                  Connect a DHIS2 instance →
                </Link>
              </div>
            </div>
          ) : (
            <div className="stat-grid">
              <div className="stat">
                <div className="stat-label">Tally metrics</div>
                <div className="stat-value">{tally.tallyCount}</div>
                <div className="stat-hint">auto-generated from metadata</div>
              </div>
              <div className="stat">
                <div className="stat-label">Aggregate buckets</div>
                <div className="stat-value">{tally.bucketCount}</div>
                <div className="stat-hint">total value {tally.bucketTotal.toLocaleString()}</div>
              </div>
              <div className="stat">
                <div className="stat-label">Indicators</div>
                <div className="stat-value">{tally.indicatorCount}</div>
                <div className="stat-hint">{tally.publishedIndicatorCount} published</div>
              </div>
            </div>
          )}
        </section>
      )}

      {wsDashboard && (
        <section className="section">
          <div className="section-head"><h2>Workspace scope</h2></div>
          <div className="stat-grid">
            <div className="stat">
              <div className="stat-label">Workspace</div>
              <div className="stat-value">{wsDashboard.workspace.name}</div>
              <div className="stat-hint">{wsDashboard.workspace.status}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Plan</div>
              <div className="stat-value">{wsDashboard.plan?.name ?? "—"}</div>
              <div className="stat-hint">{wsDashboard.subscription?.status ?? "no subscription"}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Seats</div>
              <div className="stat-value">{wsDashboard.usage.seats}</div>
              <div className="stat-hint">limit {wsDashboard.limits.seats ?? "—"}</div>
            </div>
            <div className="stat">
              <div className="stat-label">DHIS2 instances</div>
              <div className="stat-value">{wsDashboard.usage.dhis2Instances}</div>
              <div className="stat-hint">limit {wsDashboard.limits.dhis2Instances ?? "—"}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Public portals</div>
              <div className="stat-value">{wsDashboard.usage.publicPortals}</div>
              <div className="stat-hint">limit {wsDashboard.limits.publicPortals ?? "—"}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Recent audit</div>
              <div className="stat-value">{wsDashboard.recentAudit.length}</div>
              <div className="stat-hint">events</div>
            </div>
          </div>
        </section>
      )}

      <section className="section">
        <div className="section-head"><h2>SaaS tenancy</h2></div>
        {saas ? (
          <div className="stat-grid">
            {statsFromSaas(saas).map((s) => (
              <div key={s.label} className="stat">
                <div className="stat-label">{s.label}</div>
                <div className="stat-value">{s.value}</div>
                {s.hint && <div className="stat-hint">{s.hint}</div>}
              </div>
            ))}
          </div>
        ) : loading ? <SkeletonTable rows={5} columns={4} /> : <div className="empty">No tenancy data available.</div>}
      </section>

      <section className="section">
        <div className="section-head"><h2>Pipeline</h2></div>
        {overview ? (
          <div className="stat-grid">
            {statsFromOverview(overview).map((s) => (
              <div key={s.label} className="stat">
                <div className="stat-label">{s.label}</div>
                <div className="stat-value">{s.value}</div>
              </div>
            ))}
          </div>
        ) : loading ? <SkeletonTable rows={5} columns={4} /> : <div className="empty">No pipeline data available.</div>}
      </section>

      <section className="section">
        <div className="section-head">
          <h2>Recent jobs</h2>
          <Link className="linkbtn" to="/app/jobs">View all</Link>
        </div>
        {loading ? (
          <SkeletonTable rows={5} columns={4} />
        ) : recentJobs.length === 0 ? (
          <div className="empty">No jobs have run yet.</div>
        ) : (
          <div className="table-scroll">
            <TableToolbar
            hideExport
              search={recentJobsTable.search}
              setSearch={recentJobsTable.setSearch}
              page={recentJobsTable.page}
              setPage={recentJobsTable.setPage}
              pageSize={recentJobsTable.pageSize}
              setPageSize={recentJobsTable.setPageSize}
              totalPages={recentJobsTable.totalPages}
              totalRows={recentJobsTable.totalRows}
            />
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Created</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {recentJobsTable.paged.map((j) => (
                  <tr key={j.id}>
                    <td><code>{j.id}</code></td>
                    <td>{j.type ?? "—"}</td>
                    <td>{jobPill(j.status)}</td>
                    <td>{typeof j.progress === "number" ? `${Math.round(j.progress)}%` : "—"}</td>
                    <td className="soft">{j.createdAt ? new Date(j.createdAt).toLocaleString() : "—"}</td>
                    <td className="soft">{j.updatedAt ? new Date(j.updatedAt).toLocaleString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
